/**
 * Manages storage backend switching and provides unified access.
 * Singleton instance exported as `storage`.
 *
 * Supports two storage backends:
 * - 'cloud': Cloud storage with user authentication (default)
 * - 'browser': Browser-based IndexedDB storage (fallback)
 */

import type { StorageBackend } from './StorageBackend.js';
import type {
  ListFilesResult,
  ReadFileResult,
  ReadComponentResult,
  SaveResult,
  CreateResult,
  DeleteResult,
  RenameResult,
  StorageMetadata,
  StorageListener,
  StorageEventType,
  ProjectFileContent,
} from './types.js';

type BackendName = 'cloud' | 'browser';

class StorageManager {
  private backends: Map<string, StorageBackend> = new Map();
  private currentBackend: BackendName = 'cloud';
  private listeners: StorageListener[] = [];

  /**
   * Get the currently active backend
   */
  get backend(): StorageBackend {
    const backend = this.backends.get(this.currentBackend);
    if (!backend) {
      throw new Error(`No backend registered for '${this.currentBackend}'`);
    }
    return backend;
  }

  /**
   * Get current backend name
   */
  get currentBackendName(): BackendName {
    return this.currentBackend;
  }

  /**
   * Switch to a different storage backend
   */
  async switchBackend(name: BackendName): Promise<void> {
    const backend = this.backends.get(name);
    if (!backend) {
      throw new Error(`Unknown backend: ${name}`);
    }

    const available = await backend.isAvailable();
    if (!available) {
      throw new Error(`Backend '${name}' is not available`);
    }

    const previousBackend = this.currentBackend;
    this.currentBackend = name;
    this.notifyListeners('switch', { from: previousBackend, to: name });
  }

  /**
   * Register a new storage backend
   */
  registerBackend(name: string, backend: StorageBackend): void {
    this.backends.set(name, backend);
    this.notifyListeners('register', { name });
  }

  /**
   * Check if a backend is registered
   */
  hasBackend(name: string): boolean {
    return this.backends.has(name);
  }

  /**
   * Get list of available backend names
   */
  getAvailableBackends(): string[] {
    return Array.from(this.backends.keys());
  }

  /**
   * Get backend by name (for configuration)
   */
  getBackend(name: string): StorageBackend | undefined {
    return this.backends.get(name);
  }

  /**
   * Register a callback for storage events
   */
  onStorageChange(callback: StorageListener): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners of an event
   */
  private notifyListeners(event: StorageEventType, data: { from?: string; to?: string; name?: string }): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (err) {
        console.error('Storage listener error:', err);
      }
    });
  }

  // Delegate all operations to current backend
  // These methods mirror StorageBackend interface

  listFiles(): Promise<ListFilesResult> {
    return this.backend.listFiles();
  }

  readFile(path: string): Promise<ReadFileResult> {
    return this.backend.readFile(path);
  }

  readComponent(name: string): Promise<ReadComponentResult> {
    return this.backend.readComponent(name);
  }

  getAllContents(): Promise<ProjectFileContent[]> {
    return this.backend.getAllContents();
  }

  saveFile(path: string, content: string): Promise<SaveResult> {
    return this.backend.saveFile(path, content);
  }

  createFile(path: string, content?: string): Promise<CreateResult> {
    return this.backend.createFile(path, content);
  }

  createComponent(name: string, content?: string): Promise<CreateResult> {
    return this.backend.createComponent(name, content);
  }

  deleteFile(path: string): Promise<DeleteResult> {
    return this.backend.deleteFile(path);
  }

  deleteComponent(name: string): Promise<DeleteResult> {
    return this.backend.deleteComponent(name);
  }

  renameFile(oldPath: string, newPath: string): Promise<RenameResult> {
    return this.backend.renameFile(oldPath, newPath);
  }

  renameComponent(oldName: string, newName: string): Promise<RenameResult> {
    return this.backend.renameComponent(oldName, newName);
  }

  isAvailable(): Promise<boolean> {
    return this.backend.isAvailable();
  }

  getMetadata(): Promise<StorageMetadata> {
    return this.backend.getMetadata();
  }
}

// Export singleton instance
export const storage = new StorageManager();
